/**
 * Helpers shared by the Operations heatmap (and any future per-workload
 * widget). Pure functions only — no React, no API calls. Each helper has
 * unit-test coverage in workloadMapping.test.ts.
 */

import {
  type CollectionHealth,
  type M365Workload,
  type WorkloadSummary,
} from '@/types'

export type HeatLevel = 'l1' | 'l2' | 'l3' | 'l4' | 'l5'

export interface SeverityBuckets {
  critical: number
  high: number
  medium: number
  low: number
}

const HEAT_RANK: Record<HeatLevel, number> = {
  l1: 1,
  l2: 2,
  l3: 3,
  l4: 4,
  l5: 5,
}

export function heatRank(level: HeatLevel): number {
  return HEAT_RANK[level]
}

// M365DSC resource components are PascalCase prefixed with the workload
// area, e.g. AADConditionalAccessPolicy / EXOMailboxPlan / TeamsMessagingPolicy.
// Order matters — first match wins, so longer / more-specific prefixes go
// before short ones (e.g. EntraID before nothing).
const COMPONENT_PREFIX_TO_WORKLOAD: ReadonlyArray<readonly [RegExp, M365Workload]> = [
  [/^AAD/i,       'Identity'],
  [/^EntraID/i,   'Identity'],
  [/^Identity/i,  'Identity'],
  [/^EXO/i,       'Exchange'],
  [/^Exchange/i,  'Exchange'],
  [/^Teams/i,     'Teams'],
  [/^SPO/i,       'SharePoint'],
  [/^OneDrive/i,  'SharePoint'],
  // OD prefix is ambiguous (could be OneDrive, could be OData) — only
  // match it when the first non-OD char is uppercase, which catches
  // ODSettings / ODSharing without grabbing arbitrary tokens.
  [/^OD[A-Z]/,    'SharePoint'],
  [/^Defender/i,  'Defender'],
  [/^Intune/i,    'Defender'],
  [/^MDO/i,       'Defender'],
]

export function getWorkloadForDriftComponent(component: string | null | undefined): M365Workload {
  if (!component) return 'Other'
  for (const [re, wl] of COMPONENT_PREFIX_TO_WORKLOAD) {
    if (re.test(component)) return wl
  }
  return 'Other'
}

// Drift severity is a four-bucket count. Heat rule: highest non-zero bucket
// drives the level, no exceptions — one Critical drift is more important than
// fifty Low drifts on the same workload.
export function driftSeverityLevel(b: SeverityBuckets): HeatLevel {
  if (b.critical > 0) return 'l5'
  if (b.high     > 0) return 'l4'
  if (b.medium   > 0) return 'l3'
  if (b.low      > 0) return 'l2'
  return 'l1'
}

// BPA severity for a workload. Returns null when the workload doesn't appear
// in the summary at all (i.e. has never been evaluated) — the heatmap renders
// these cells as gaps rather than fake-healthy l1 swatches.
export function bpaSeverityLevel(s: WorkloadSummary | undefined | null): HeatLevel | null {
  if (!s) return null
  if (s.controls === 0) return null
  if (s.critical > 0) return 'l5'
  if (s.high     > 0) return 'l4'
  if (s.medium   > 0) return 'l3'
  return 'l1'
}

const STALE_MS = 24 * 60 * 60 * 1000

// Coverage health: collection failures > collection errors > staleness > healthy.
// `now` is injectable for tests.
export function coverageHealthLevel(h: CollectionHealth, now: number = Date.now()): HeatLevel {
  const graphMissing      = h.graphExpected      && !h.graphCollected
  const powerShellMissing = h.powerShellExpected && !h.powerShellCollected
  if (graphMissing || powerShellMissing) return 'l5'

  if (h.errors && h.errors.length > 0) return 'l4'

  const newest = newestCollectedAt(h)
  if (newest != null && now - newest > STALE_MS) return 'l3'

  return 'l1'
}

function newestCollectedAt(h: CollectionHealth): number | null {
  const candidates = [h.graphCollectedAt, h.powerShellCollectedAt]
    .map(t => (t ? new Date(t).getTime() : null))
    .filter((t): t is number => t != null && !Number.isNaN(t))
  return candidates.length > 0 ? Math.max(...candidates) : null
}
